<template>
  <v-container v-if="item">
    <toolbar mode="export" :item="item" @clone="onClone" @delete="onDelete">
      <ax-button v-if="isOwner(item)" icon="mdi-refresh" tooltip="Refresh" confirm @confirmed="onUpdate" />
      <!-- A text-format export (currently just ARIES) has no xlsx --
           export_to_excel() is a DataFrame-only concept -- so its download
           always goes through the download endpoint directly instead. -->
      <v-btn v-if="isTextFormat" icon @click="onDownload"><v-icon>mdi-arrow-down</v-icon></v-btn>
      <v-btn v-else icon :href="item.xlsx" :disabled="!item.xlsx"><v-icon>mdi-arrow-down</v-icon></v-btn>
    </toolbar>
    <v-card flat>
      <v-tabs v-model="tab">
        <v-tab :disabled="item.status !== 'SUCCESS'" href="#data">Data</v-tab>
        <v-tab v-if="!isTextFormat" :disabled="item.status !== 'SUCCESS'" href="#info">Info</v-tab>
        <v-tab href="#result">Result</v-tab>
        <!-- <v-tab href="#logs">Logs</v-tab> -->
        <v-tab href="#details">Details</v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="tab">
        <v-tab-item value="data">
          <!-- Plain-text export -- no columns to speak of, so this is a
               scrollable monospace view of the raw ARIES text (paginated
               the same "load more" way frame-head does) rather than a
               table. -->
          <v-card-text v-if="isTextFormat">
            <pre class="ax-text-preview">{{ textLines.join('\n') }}</pre>
            <v-btn v-if="!textLoaded" text :loading="textLoading" @click="onLoadText">Load more</v-btn>
          </v-card-text>
          <frame-head v-else :items="head" @load="onLoad" />
        </v-tab-item>
        <v-tab-item v-if="!isTextFormat" value="info">
          <frame-info :items="info" />
        </v-tab-item>
        <v-tab-item value="result">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Task</v-col>
              <v-col>{{ item.result.task_name }}</v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Status</v-col>
              <v-col>{{ item.result.status }}</v-col>
            </v-row>
            <v-row dense v-if="item.result.status == 'FAILURE'">
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Result</v-col>
              <v-col>{{ JSON.parse(item.result.result)['exc_type'] }}: {{ JSON.parse(item.result.result)['exc_message'].join(', ') }}</v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Completed</v-col>
              <v-col>{{ item.result.date_done | dateFormat("LL LT") }} ({{ item.result.date_done | dateFormat }})</v-col>
            </v-row>
            <v-row dense v-if="user.is_superuser">
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Traceback</v-col>
              <v-col><pre>{{ item.result.traceback }}</pre></v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>
        <!-- <v-tab-item value="logs">
          <v-card-text>
            <pre>{{ item.history || 'No logs available' }}</pre>
          </v-card-text>
        </v-tab-item> -->
        <v-tab-item value="details">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Name</v-col>
              <v-col>
                <template v-if="item.file">
                  {{ item.file.name }} ({{ item.file.size | byteFormat }})
                </template>
                <template v-else>{{ item.uuid }}</template>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Owner</v-col>
              <v-col>{{ item.owner.name }}</v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Created</v-col>
              <v-col>{{ item.created_at | dateFormat("LL LT") }} ({{ item.created_at | dateFormat }})</v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="2" md="1" class="font-weight-bold">Updated</v-col>
              <v-col>{{ item.updated_at | dateFormat("LL LT") }} ({{ item.updated_at | dateFormat }})</v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import AxButton from "@/components/button"
import AxStatus from "@/components/status"
import frameHead from "@/components/head"
import frameInfo from "@/components/info"
import toolbar from "@/components/toolbar"
import Exports from "@/api/exports"
export default {
  name: "Export",
  components: {
    frameHead,
    frameInfo,
    toolbar,
    AxButton,
    AxStatus
  },
  data() {
    return {
      item: null,
      head: {
        data: [],
        schema: {},
      },
      info: {
        data: [],
        schema: {}
      },
      headLoaded: false,
      headLoading: false,
      textLines: [],
      textLoaded: false,
      textLoading: false,
    }
  },
  created() {
    Exports.retrieve(this.$route.params.uuid).then(response => {
      this.item = response.data
      if (this.item.status == 'SUCCESS') {
        if (this.isTextFormat) {
          this.onLoadText()
        } else {
          Exports.info(this.$route.params.uuid).then(response => this.info = typeof response.data == "string" ? JSON.parse(response.data) : response.data)
          this.onLoad()
        }
      }
    })
  },
  computed: {
    tab: {
      set (tab) {
        this.$router.replace({ hash: tab })
      },
      get () {
        return this.$route.hash.replace('#', '')
      }
    },
    // Currently just ARIES -- see Export._is_text_format() on the backend,
    // which this mirrors.
    isTextFormat() {
      return !!(this.item && this.item.config && this.item.config.format == 'aries')
    },
  },
  methods: {
    onLoad() {
      if (this.headLoaded || this.headLoading) {
        return
      }
      
      this.headLoading = true
      Exports.head(this.$route.params.uuid, this.head.data.length)
        .then((response) => {
          let head = typeof response.data == "string" ? JSON.parse(response.data) : response.data
          if (head.data && head.data.length > 0) {
            if (this.head.data.length === 0) {
              this.$set(this.head, 'data', head.data)
            } else {
              this.head.data.push(...head.data)
            }
            if (head.schema && Object.keys(head.schema).length > 0) {
              this.$set(this.head, 'schema', head.schema)
            }
            if (head.data.length < 50) {
              this.headLoaded = true
            }
          } else {
            this.headLoaded = true
          }
        })
        .finally(() => {
          this.headLoading = false
        })
    },
    onUpdate() {
      Exports.refresh(this.$route.params.uuid).then(_ => {
        this.notify('Refreshing export...')
        this.$router.push({ name: 'exportList'})
    })
    },
    onLoadText() {
      if (this.textLoaded || this.textLoading) {
        return
      }

      this.textLoading = true
      Exports.head(this.$route.params.uuid, this.textLines.length)
        .then((response) => {
          let head = typeof response.data == "string" ? JSON.parse(response.data) : response.data
          let lines = head.lines || []
          if (lines.length > 0) {
            this.textLines.push(...lines)
          }
          if (lines.length < 50 || this.textLines.length >= head.total) {
            this.textLoaded = true
          }
        })
        .finally(() => {
          this.textLoading = false
        })
    },
    onDownload() {
      // A text-format export's own content (leading-BOM CSV assumption
      // below doesn't apply to it) is served back byte-exact -- see
      // Export._is_text_format()/get_text() and the download view's own
      // branch on the backend.
      Exports.download(this.$route.params.uuid).then(response => {
        let content = response.data
        let ext = this.isTextFormat ? 'txt' : 'csv'
        let blobParts = this.isTextFormat ? [content] : ["\ufeff", content]
        var downloadLink = document.createElement("a")
        var blob = new Blob(blobParts)
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `${this.$route.params.uuid}.${ext}`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      })
    },
    onClone(){
      Exports.clone(this.$route.params.uuid).then(_ => {
        this.notify('Export cloned')
        this.$router.push({ name: 'exportList' } )
      })
    },
    onDelete() {
      Exports.destroy(this.$route.params.uuid).then(_ => {
        this.notify('Export deleted')
        this.$router.push({ name: 'exportList' })
      })
    }
  }
}
</script>
<style scoped>
.ax-text-preview {
  white-space: pre;
  overflow-x: auto;
  font-family: monospace;
  font-size: 12px;
}
</style>
